import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BankLayout from './BankLayout';
import { getCaptcha, loginUser } from './apiClient';

function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const hintUser = useMemo(() => 'demo.user', []);

  const loadCaptcha = async () => {
    const captcha = await getCaptcha();
    setCaptchaCode(captcha.captchaCode);
    setCaptchaToken(captcha.captchaToken);
    setCaptchaInput('');
  };

  React.useEffect(() => {
    loadCaptcha().catch(() => {
      setError('Unable to load captcha. Refresh the page or retry.');
    });
  }, []);

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Enter your username and password to continue.');
      return;
    }

    if (!captchaInput.trim() || !captchaToken) {
      setError('Enter the captcha exactly as shown.');
      return;
    }

    setIsSubmitting(true);

    try {
      await loginUser({
        username: username.trim(),
        password: password.trim(),
        captchaToken,
        captchaAnswer: captchaInput.trim(),
      });
      navigate('/account');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Login failed. Try again.');
      await loadCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BankLayout
      pageTitle="Personal Banking Login"
      strapline="Access account summaries, manage beneficiaries, and review recent activity from a familiar retail banking interface."
    >
      <section style={styles.grid}>
        <div style={styles.mainCard}>
          <div style={styles.cardHeader}>Existing User Login</div>
          <div style={styles.cardBody}>
            <div style={styles.notice}>
              Use demo credentials such as <b>{hintUser}</b> with any password to enter the portal.
            </div>

            <form onSubmit={handleSubmit}>
              <label style={styles.label}>
                Username / CIF Number
                <input
                  style={styles.input}
                  value={username}
                  onChange={event => setUsername(event.target.value)}
                  placeholder="Enter username or CIF"
                />
              </label>

              <label style={styles.label}>
                Login Password
                <input
                  style={styles.input}
                  type="password"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  placeholder="Enter password"
                />
              </label>

              <label style={styles.label}>
                Captcha Verification
                <div style={styles.captchaRow}>
                  <div style={styles.captchaBox}>{captchaCode}</div>
                  <button
                    type="button"
                    style={styles.captchaRefresh}
                    onClick={() => loadCaptcha().catch(() => setError('Unable to refresh captcha.'))}
                  >
                    Refresh
                  </button>
                </div>
                <input
                  style={styles.input}
                  value={captchaInput}
                  onChange={event => setCaptchaInput(event.target.value)}
                  placeholder="Enter captcha"
                />
              </label>

              {error ? <div style={styles.error}>{error}</div> : null}

              <button type="submit" style={styles.primaryButton}>
                {isSubmitting ? 'Signing In...' : 'Login'}
              </button>
            </form>

            <div style={styles.inlineLinks}>
              <Link to="/signup" style={styles.linkButton}>
                New User? Register Here
              </Link>
              <a href="#security" style={styles.secondaryLink}>
                Security Tips
              </a>
            </div>
          </div>
        </div>

        <aside style={styles.sidePanel}>
          <div style={styles.sidePanelHeader}>Important Information</div>
          <div style={styles.sidePanelBody}>
            <ul style={styles.infoList}>
              <li>Never share internet banking passwords or OTPs with anyone.</li>
              <li>Prefer adding trusted payees only after verifying account details.</li>
              <li>Review last login and recent transactions after every sign-in.</li>
            </ul>

            <div id="security" style={styles.warningBox}>
              High-risk operations are protected with beneficiary cooling periods in live banking systems. This demo focuses on the UI flow only.
            </div>
          </div>
        </aside>
      </section>
    </BankLayout>
  );
}

const styles = {
  grid: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: 'minmax(0, 1.6fr) minmax(280px, 1fr)',
  },
  mainCard: {
    background: '#fff',
    border: '1px solid var(--sbi-border)',
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 12px 30px rgba(26, 79, 156, 0.08)',
  },
  cardHeader: {
    background: 'linear-gradient(180deg, #2a63b0 0%, var(--sbi-blue) 100%)',
    color: '#fff',
    fontSize: 18,
    fontWeight: 700,
    padding: '14px 18px',
  },
  cardBody: {
    padding: 22,
  },
  notice: {
    background: '#f6f9ff',
    border: '1px solid #d7e3f7',
    borderRadius: 10,
    color: 'var(--sbi-muted)',
    fontSize: 14,
    marginBottom: 18,
    padding: '12px 14px',
  },
  label: {
    color: 'var(--sbi-deep-blue)',
    display: 'block',
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 14,
  },
  input: {
    border: '1px solid #b7c7e3',
    borderRadius: 8,
    display: 'block',
    marginTop: 8,
    padding: '12px 13px',
    width: '100%',
  },
  captchaRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 12,
    marginTop: 8,
    marginBottom: 8,
  },
  captchaBox: {
    background: 'linear-gradient(135deg, #eef3fc 0%, #dfe9fb 100%)',
    border: '1px dashed #8ca9d7',
    borderRadius: 8,
    color: 'var(--sbi-deep-blue)',
    fontFamily: '"Courier New", monospace',
    fontSize: 22,
    fontStyle: 'italic',
    fontWeight: 700,
    letterSpacing: 4,
    padding: '10px 16px',
    textDecoration: 'line-through',
  },
  captchaRefresh: {
    background: '#fff',
    border: '1px solid #b4c6e5',
    borderRadius: 8,
    color: 'var(--sbi-blue)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    padding: '10px 12px',
  },
  error: {
    color: '#c43b23',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 12,
  },
  primaryButton: {
    background: 'linear-gradient(180deg, #f2ba52 0%, var(--sbi-gold) 100%)',
    border: '1px solid #cb8f1b',
    borderRadius: 8,
    color: '#21314d',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 700,
    padding: '12px 22px',
  },
  inlineLinks: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 18,
  },
  linkButton: {
    color: 'var(--sbi-blue)',
    fontSize: 14,
    fontWeight: 700,
    textDecoration: 'none',
  },
  secondaryLink: {
    color: 'var(--sbi-muted)',
    fontSize: 14,
    textDecoration: 'none',
  },
  sidePanel: {
    background: '#fff',
    border: '1px solid var(--sbi-border)',
    borderRadius: 14,
    overflow: 'hidden',
  },
  sidePanelHeader: {
    background: '#edf3ff',
    borderBottom: '1px solid var(--sbi-border)',
    color: 'var(--sbi-deep-blue)',
    fontSize: 17,
    fontWeight: 700,
    padding: '14px 18px',
  },
  sidePanelBody: {
    padding: 18,
  },
  infoList: {
    color: 'var(--sbi-text)',
    lineHeight: 1.6,
    margin: '0 0 18px',
    paddingLeft: 20,
  },
  warningBox: {
    background: '#fff6eb',
    border: '1px solid #f1cfaa',
    borderRadius: 10,
    color: 'var(--sbi-warning)',
    fontSize: 13,
    lineHeight: 1.55,
    padding: '12px 14px',
  },
};

export default LoginPage;
